import api from '../api';

// Helper function để tạo headers với token
const getAuthHeaders = (token) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

// Admin Design Case API functions
export const adminDesignCaseApi = {
  // Lấy tất cả design cases (admin có thể xem tất cả)
  getAllDesignCases: async (token, params = {}) => {
    try {
      const response = await api.get('/design-cases', {
        headers: getAuthHeaders(token),
        params
      });
      return response;
    } catch (error) {
      console.error('Error getting all design cases:', error);
      throw error;
    }
  },

  // Lấy chi tiết design case
  getDesignCaseById: async (token, caseId) => {
    try {
      const response = await api.get(`/design-cases/${caseId}`, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error getting design case by ID:', error);
      throw error;
    }
  },

  // Cập nhật design case (admin có thể edit)
  updateDesignCase: async (token, caseId, caseData) => {
    try {
      const response = await api.put(`/admin/design-cases/${caseId}`, caseData, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error updating design case:', error);
      throw error;
    }
  },

  // Xóa design case
  deleteDesignCase: async (token, caseId) => {
    try {
      const response = await api.delete(`/admin/design-cases/${caseId}`, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error deleting design case:', error);
      throw error;
    }
  },

  // Force approve design case
  forceApproveCase: async (token, caseId, approvalData = {}) => {
    try {
      const response = await api.put(`/design-cases/${caseId}/force-approve`, approvalData, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error force approving case:', error);
      throw error;
    }
  },

  // Force reject design case
  forceRejectCase: async (token, caseId, rejectionData = {}) => {
    try {
      const response = await api.put(`/admin/design-cases/${caseId}/force-reject`, rejectionData, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error force rejecting case:', error);
      throw error;
    }
  },

  // Cancel design case
  cancelDesignCase: async (token, caseId) => {
    try {
      const response = await api.put(`/admin/design-cases/${caseId}/cancel`, {}, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error cancelling design case:', error);
      throw error;
    }
  },

  // Assign designer cho case
  assignDesigner: async (token, caseId, designerId) => {
    try {
      const response = await api.put(`/admin/design-cases/${caseId}/assign`, {
        designerId
      }, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error assigning designer:', error);
      throw error;
    }
  },

  // Unassign designer từ case
  unassignDesigner: async (token, caseId) => {
    try {
      const response = await api.put(`/admin/design-cases/${caseId}/unassign`, {}, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error unassigning designer:', error);
      throw error;
    }
  },

  // Lấy thống kê design cases
  getDesignCaseStats: async (token, timeRange = 'all') => {
    try {
      const response = await api.get('/admin/design-cases/stats', {
        headers: getAuthHeaders(token),
        params: { timeRange }
      });
      return response;
    } catch (error) {
      console.error('Error getting design case stats:', error);
      throw error;
    }
  },

  // Lấy dashboard data cho admin
  getAdminDashboard: async (token, timeRange = 'today') => {
    try {
      const response = await api.get('/admin/dashboard', {
        headers: getAuthHeaders(token),
        params: { timeRange }
      });
      return response;
    } catch (error) {
      console.error('Error getting admin dashboard:', error);
      throw error;
    }
  },

  // Lấy danh sách designers
  getDesigners: async (token, params = {}) => {
    try {
      const response = await api.get('/admin/designers', {
        headers: getAuthHeaders(token),
        params
      });
      return response;
    } catch (error) {
      console.error('Error getting designers:', error);
      throw error;
    }
  },

  // Lấy chi tiết designer
  getDesignerById: async (token, designerId) => {
    try {
      const response = await api.get(`/admin/designers/${designerId}`, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error getting designer by ID:', error);
      throw error;
    }
  },

  // Cập nhật designer
  updateDesigner: async (token, designerId, designerData) => {
    try {
      const response = await api.put(`/admin/designers/${designerId}`, designerData, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error updating designer:', error);
      throw error;
    }
  },

  // Xóa designer
  deleteDesigner: async (token, designerId) => {
    try {
      const response = await api.delete(`/admin/designers/${designerId}`, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error deleting designer:', error);
      throw error;
    }
  },

  // Lấy danh sách sellers
  getSellers: async (token, params = {}) => {
    try {
      const response = await api.get('/admin/sellers', {
        headers: getAuthHeaders(token),
        params
      });
      return response;
    } catch (error) {
      console.error('Error getting sellers:', error);
      throw error;
    }
  },

  // Lấy chi tiết seller
  getSellerById: async (token, sellerId) => {
    try {
      const response = await api.get(`/admin/sellers/${sellerId}`, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error getting seller by ID:', error);
      throw error;
    }
  },

  // Lấy notifications cho admin
  getAdminNotifications: async (token) => {
    try {
      const response = await api.get('/admin/notifications', {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error getting admin notifications:', error);
      throw error;
    }
  },

  // Mark notification as read
  markNotificationAsRead: async (token, notificationId) => {
    try {
      const response = await api.put(`/admin/notifications/${notificationId}/read`, {}, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  // Mark all notifications as read
  markAllNotificationsAsRead: async (token) => {
    try {
      const response = await api.put('/admin/notifications/read-all', {}, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  // Export design cases data
  exportDesignCases: async (token, params = {}) => {
    try {
      const response = await api.get('/admin/design-cases/export', {
        headers: getAuthHeaders(token),
        params,
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      console.error('Error exporting design cases:', error);
      throw error;
    }
  },

  // Bulk operations
  bulkUpdateCases: async (token, caseIds, updateData) => {
    try {
      const response = await api.put('/admin/design-cases/bulk-update', {
        caseIds,
        updateData
      }, {
        headers: getAuthHeaders(token)
      });
      return response;
    } catch (error) {
      console.error('Error bulk updating cases:', error);
      throw error;
    }
  },

  // Bulk delete cases
  bulkDeleteCases: async (token, caseIds) => {
    try {
      const response = await api.delete('/admin/design-cases/bulk-delete', {
        headers: getAuthHeaders(token),
        data: { caseIds }
      });
      return response;
    } catch (error) {
      console.error('Error bulk deleting cases:', error);
      throw error;
    }
  }
}; 